import { isConsumerKind, TrafficAccelerator, } from "gather-video-common/dist/src/public/types";
import { getCandidateAddresses } from "../../metrics/RTCStatsManager";
import { TransportConnectionStateHelper } from "./TransportConnectionStateHelper";
import { metricsManager as globalsMetricsManager } from "../../Globals";
import { Logger } from "../../Logger";
import { metricsManager } from "../../metrics/MetricsManager";
import { buildErrorContext, ErrorContext } from "gather-common/dist/src/public/utils";
import { parseRTCStats } from "../../metrics/rtcStats";
import _ from "lodash";
import { uniq } from "ramda";
import { WebRTCTransportErrorCode } from "../../EventEmitter/WebRTCTransportEvents";
import { CLIENT_TURN_REFRESH_TIME_S, CF_ICE_ORIG_IPS_DNS_BASE, } from "gather-video-common/dist/src/public/turn";
import { IceRestartReason } from "./IceRestartReason";
export class TransportProduceError extends Error {
    constructor(message) {
        super(message);
        this.name = "TransportProduceError";
        Object.setPrototypeOf(this, TransportProduceError.prototype);
    }
}
export class SignaledTransport {
    eventEmitter;
    serverURL;
    signaledDevice;
    signaling;
    transport;
    connectionStateHelper;
    nextProduceTag;
    screenShareHighQuality = false;
    encodingParamsSize;
    iceTransportPolicy;
    _availableBitrate;
    created = false;
    failedToConnect = false;
    debugPrefix;
    isIceRestartPending = false;
    iceRestartReason = IceRestartReason.Unknown;
    iceTransportRequestOptions;
    direction;
    rtcStatsManager;
    shouldRetryConsumeRequest = false;
    isFirstAttempt;
    currentConnectionState;
    connectingStartTime;
    pendingConsumers = {};
    refreshTURNCredentialsTimer;
    get id() {
        return this.transport?.id ?? "UNKNOWN";
    }
    turnProvider;
    constructor(serverURL, signaling, device, eventEmitter, direction, rtcStatsManager, iceTransportRequestOptions, turnProvider) {
        this.eventEmitter = eventEmitter;
        this.serverURL = serverURL;
        this.signaling = signaling;
        this.signaledDevice = device;
        this.debugPrefix = `[SFU ${serverURL}/${direction}]`;
        this.rtcStatsManager = rtcStatsManager;
        this.iceTransportRequestOptions = iceTransportRequestOptions;
        this.direction = direction;
        this.connectionStateHelper = new TransportConnectionStateHelper(this.serverURL, this.eventEmitter);
        this.isFirstAttempt = true;
        this.turnProvider = turnProvider;
    }
    destroy() {
        if (this.isFirstAttempt) {
            this.recordFirstAttemptResult(false);
        }
        if (this.transport && !this.transport.closed) {
            this.transport.close();
            this.signaling?.emit("transport-close", {
                transportId: this.transport.id,
            });
        }
        if (this.refreshTURNCredentialsTimer) {
            clearTimeout(this.refreshTURNCredentialsTimer);
            this.refreshTURNCredentialsTimer = undefined;
        }
        this.created = false;
        this.clearPendingIceRestart();
        this.connectionStateHelper.destroy();
    }
    onMediasoupTransportConnect = async ({ dtlsParameters }, onSuccess, onError) => {
        if (!this.transport) {
            Logger.error("Should never happen. onMediasoupTransportConnect called without a transport");
            return;
        }
        const transportId = this.transport.id;
        const direction = this.transport.direction;
        const handleTransportConnectError = () => {
            if (this.transport) {
                this.transport.close();
            }
            if (this.signaling) {
                this.signaling.emit("transport-close", { transportId });
            }
            this.created = false;
            if (direction === "recv") {
                this.shouldRetryConsumeRequest = true;
            }
        };
        if (!this.signaling) {
            const errorMessage = "websocket signaling is not available";
            Logger.error(errorMessage);
            onError(errorMessage);
            return;
        }
        const res = await this.signaling
            .sendWithResponse("transport-connect", {
            transportId,
            dtlsParameters,
        })
            .catch((e) => {
            Logger.error(`sendWithResponse("transport-connect") failed`, buildErrorContext(e));
            handleTransportConnectError();
            onError(e);
        });
        if (res && res.error) {
            Logger.error(`sendWithResponse("transport-connect") received error`, res.error);
            handleTransportConnectError();
            onError(res.error);
        }
        else {
            onSuccess();
            this.connectionStateHelper.startNewConnectionStateMonitorTimer(this.id, "initialConnect");
        }
    };
    onMediasoupTransportProduce = async ({ kind, rtpParameters, }, onSuccess, onError) => {
        if (!this.transport) {
            Logger.error("Should never happen. onMediasoupTransportProduce called without a transport");
            return;
        }
        const transportId = this.transport.id;
        const tag = this.nextProduceTag;
        if (!tag) {
            const errorMessage = '"produce" event received with no nextProduceTag set!';
            Logger.error(errorMessage);
            onError(errorMessage);
            return;
        }
        if (!this.signaling) {
            onError("Signaling is unavailable");
            return;
        }
        const res = await this.signaling
            .sendWithResponse("produce", {
            transportId,
            tag,
            kind,
            rtpParameters,
            highQualityScreenShare: this.screenShareHighQuality,
        })
            .catch((e) => {
            Logger.error(`sendWithResponse("produce") failed`, buildErrorContext(e));
            onError(e);
            return;
        });
        if (res) {
            if ("error" in res) {
                onError(res.error);
            }
            else {
                onSuccess(res);
            }
        }
        else {
        }
    };
    onMediasoupConnectionStateChange = (state) => {
        Logger.log(`${this.debugPrefix} transport connection state change ${this.currentConnectionState ?? "none"} -> ${state}`);
        this.currentConnectionState = state;
        metricsManager.mediaInterruption.mediaInterruptedEvent(this.serverURL, this.direction, state !== "connected");
        metricsManager.connectionState.countStateChange(this.serverURL, state, this.direction);
        if (state === "connecting" && this.isFirstAttempt) {
            this.connectingStartTime = Date.now();
        }
        if (state === "connected") {
            if (this.isFirstAttempt) {
                this.recordFirstAttemptResult(true);
            }
            this.clearPendingIceRestart();
        }
        this.eventEmitter.publishWebRTCTransportConnectionStateChange({
            sfuAddr: this.serverURL,
            id: this.id,
            connectionState: state,
        });
    };
    async connect(direction) {
        if (this.created) {
            Logger.warn(`connect() called on an already connected transport`);
            return;
        }
        const connectionInfo = await this.signaling?.sendWithResponse("transport-create", {
            direction,
            iceTransportRequestOptions: this.iceTransportRequestOptions,
        });
        this.resetTURNCredentialTimer();
        if (!connectionInfo || "error" in connectionInfo)
            throw new Error(connectionInfo ? connectionInfo.error : "Signaling is unavailable");
        this.iceTransportPolicy = connectionInfo.iceTransportPolicy;
        const acceleratorUsed = connectionInfo.appData?.["trafficAccelerator"] ?? TrafficAccelerator.None;
        Logger.info(`SFU sent a candidate using traffic accelerator ${acceleratorUsed}`);
        const acceleratorRequested = this.iceTransportRequestOptions?.trafficAccelerator;
        if (acceleratorRequested && acceleratorRequested !== acceleratorUsed) {
            Logger.error(`Requested traffic accelerator ${this.iceTransportRequestOptions?.trafficAccelerator} from SFU but it used ${acceleratorUsed} instead`);
        }
        if (this.turnProvider) {
            const iceServers = connectionInfo.iceServers;
            if (iceServers) {
                const isCFIceServer = (server) => Array.isArray(server.urls) &&
                    server.urls.some((url) => url.includes("cloudflare") || url.includes(CF_ICE_ORIG_IPS_DNS_BASE));
                const matchingIceServers = iceServers.filter((server) => {
                    const wantCF = this.turnProvider === "cloudflare";
                    return wantCF === isCFIceServer(server);
                });
                Logger.info(`Using ${this.turnProvider} ice servers: ${JSON.stringify(matchingIceServers)}`);
                connectionInfo.iceServers = matchingIceServers;
            }
        }
        if (direction === "send") {
            this.transport = this.signaledDevice.createSendTransport(connectionInfo);
        }
        else {
            this.transport = this.signaledDevice.createRecvTransport(connectionInfo);
        }
        this.created = true;
        this.transport.on("connect", this.onMediasoupTransportConnect);
        this.transport.on("produce", this.onMediasoupTransportProduce);
        this.transport.on("connectionstatechange", this.onMediasoupConnectionStateChange);
    }
    async restartIce(reason) {
        if (!this.created)
            return;
        if (this.isIceRestartPending) {
            return;
        }
        if (this.isFirstAttempt) {
            this.recordFirstAttemptResult(false);
        }
        try {
            const restartIceResponse = await this.signaling?.sendWithResponse("restart-ice", {
                transportId: this.transport?.id || "",
                iceTransportRequestOptions: this.iceTransportRequestOptions,
            });
            if (!restartIceResponse)
                throw new Error("Signaling is unavailable");
            if ("error" in restartIceResponse)
                throw new Error(restartIceResponse.error);
            const { iceParameters, iceServers } = restartIceResponse;
            this.isIceRestartPending = true;
            try {
                if (iceServers.length > 0) {
                    await this.transport?.updateIceServers({ iceServers });
                }
                this.resetTURNCredentialTimer();
            }
            catch (e) {
                Logger.warn("Failed to update ice servers, expected on Firefox", e);
                if (reason === IceRestartReason.RefreshTURNCredentials) {
                    throw e;
                }
            }
            await this.transport?.restartIce({ iceParameters });
            this.iceRestartReason = reason;
            this.connectionStateHelper.startNewConnectionStateMonitorTimer(this.id, "iceRestart");
        }
        catch (error) {
            this.eventEmitter.publishWebRTCTransportError(WebRTCTransportErrorCode.ICERestartFailure, {
                sfuAddr: this.serverURL,
                id: this.id,
                error: error instanceof Error ? error : String(error),
            });
        }
    }
    recordFirstAttemptResult(success) {
        this.isFirstAttempt = false;
        globalsMetricsManager.addMetricReliably("gvc-first-transport-connect", {
            serverURL: this.serverURL,
            succeeded: success ? "true" : "false",
            ...(success && this.connectingStartTime
                ? { timeToConnect: Date.now() - this.connectingStartTime }
                : {}),
        });
        this.connectingStartTime = undefined;
    }
    async produce(tag, options, mediasoupOptions) {
        const rtpCapabilities = this.signaledDevice.device.rtpCapabilities;
        const h264Codec = rtpCapabilities.codecs?.find((codec) => codec.mimeType === "video/H264");
        const vp8Codec = rtpCapabilities.codecs?.find((codec) => codec.mimeType === "video/VP8");
        if (options.screenShareHighQuality !== undefined) {
            this.screenShareHighQuality = options.screenShareHighQuality;
        }
        const transport = this.transport;
        if (transport === undefined)
            throw Error("must call connect before produce");
        if (this.nextProduceTag)
            throw Error("can't interleave calls to produce");
        if (h264Codec) {
            if (tag === "screen") {
                mediasoupOptions.codec = h264Codec;
            }
            if (tag === "video") {
                mediasoupOptions.codec = vp8Codec;
            }
        }
        this.nextProduceTag = tag;
        if (tag === "video") {
            this.encodingParamsSize = mediasoupOptions.encodings?.length;
        }
        try {
            const producer = await transport.produce(mediasoupOptions);
            this.nextProduceTag = undefined;
            return producer;
        }
        catch (err) {
            this.nextProduceTag = undefined;
            Logger.error(`Failed to produce ${tag}`, new ErrorContext({ kind: tag, serverUrl: this.serverURL }, err));
            if (err instanceof Error && err.message && err.message.includes("RTCPeerConnection")) {
                throw err;
            }
            else {
                throw new TransportProduceError(err instanceof Error ? err.message : "failed to create produce");
            }
        }
    }
    async consume(srcId, srcStreamId, tag, spatialLayer, onConsumeDataReceived) {
        const transport = this.transport;
        if (transport === undefined)
            throw Error("must call connect before consume");
        const consumeOpts = await this.signaling?.sendWithResponse("consume", {
            transportId: transport.id,
            srcId,
            srcStreamId,
            tag,
            rtpCapabilities: this.signaledDevice.device.rtpCapabilities,
            spatialLayer,
        });
        if (!consumeOpts || "error" in consumeOpts)
            throw Error(consumeOpts ? consumeOpts.error : "sendWithResponse(consume) failed");
        onConsumeDataReceived?.(consumeOpts.producerPaused);
        const consumer = await transport.consume(consumeOpts);
        this.signaling?.emit("consume-created", { srcId, srcStreamId, tag, consumerId: consumer.id });
        return consumer;
    }
    throttledCreatePendingConsumers = _.throttle(() => {
        Object.entries(this.pendingConsumers).forEach(([srcId, onePlayerConsumers]) => {
            Object.entries(onePlayerConsumers).forEach(([tag, pending]) => {
                if (!isConsumerKind(tag))
                    return;
                try {
                    this.transport?.consume(pending.consumeOpts).then((consumer) => {
                        this.signaling?.emit("consume-created", {
                            srcId,
                            srcStreamId: pending.srcStreamId,
                            tag,
                            consumerId: consumer?.id,
                        });
                        pending.onSuccess(consumer);
                    });
                }
                catch (e) {
                    if (typeof e === "string" || e instanceof Error) {
                        pending.onFail(e);
                        this.pendingConsumers = {};
                        return;
                    }
                }
            });
        });
        this.pendingConsumers = {};
    }, 100);
    async batchConsume(srcId, srcStreamId, tag, onSuccess, onFail, spatialLayer, onConsumeDataReceived) {
        const transport = this.transport;
        if (transport === undefined)
            throw Error("must call connect before consume");
        const consumeOpts = await this.signaling?.sendWithResponse("consume", {
            transportId: transport.id,
            srcId,
            srcStreamId,
            tag,
            rtpCapabilities: this.signaledDevice.device.rtpCapabilities,
            spatialLayer,
        });
        if (!consumeOpts || "error" in consumeOpts)
            throw Error(consumeOpts ? consumeOpts.error : "sendWithResponse(consume) failed");
        onConsumeDataReceived?.(consumeOpts.producerPaused);
        const onePlayerConsumers = this.pendingConsumers[srcId];
        if (onePlayerConsumers) {
            onePlayerConsumers[tag] = { srcStreamId, consumeOpts, onSuccess, onFail };
        }
        else {
            this.pendingConsumers[srcId] = {
                [tag]: { consumeOpts, srcStreamId, onSuccess, onFail },
            };
        }
        this.throttledCreatePendingConsumers();
    }
    cancelPendingConsume(srcId) {
        delete this.pendingConsumers[srcId];
    }
    getRtpConnectionState() {
        return this.transport?.connectionState;
    }
    turnForced() {
        return this.iceTransportPolicy === "relay";
    }
    setAvailableRecvBandwidth(availableBandwidth, usedBandwidth) {
        this.rtcStatsManager.setRecvBandwidth(this.serverURL, availableBandwidth, usedBandwidth);
    }
    clearPendingIceRestart() {
        if (this.isIceRestartPending) {
            this.isIceRestartPending = false;
            globalsMetricsManager.addMetricReliably("restart-ice", {
                reason: this.iceRestartReason,
                result: this.transport?.connectionState || "unknown",
                sfu: this.serverURL,
                direction: this.direction,
            });
            this.iceRestartReason = IceRestartReason.Unknown;
        }
    }
    async summarize() {
        let connStats = {};
        try {
            connStats = await this.rtcStatsManager.getRtcConnectionStats(this.serverURL, this.direction);
        }
        catch (e) {
            Logger.error(`Failed to get connection statistics for transport`);
        }
        return {
            connectionState: this.transport?.connectionState,
            closed: this.transport?.closed,
            ...connStats,
        };
    }
    async getConnectionReport() {
        const statsReport = {};
        const transportStats = await this.transport?.getStats();
        if (transportStats !== undefined) {
            const stats = {};
            transportStats.forEach((stat) => {
                stats[stat.id] = stat;
            });
            const parsed = parseRTCStats(stats);
            const candidatePair = parsed["candidate-pair"];
            statsReport["lastPacketReceived"] = candidatePair?.lastPacketReceivedTimestamp ?? "never";
            statsReport["totalBytesReceived"] = candidatePair?.bytesReceived ?? 0;
            statsReport["codecsInUse"] = uniq(Object.values(parsed.codecs || {}))
                .sort()
                .join(" ");
            const localCandidate = parsed["local-candidate"];
            const { address, relay } = localCandidate
                ? getCandidateAddresses(localCandidate)
                : {
                    address: undefined,
                    relay: undefined,
                };
            statsReport["localCandidate"] = address ?? "undefined";
            statsReport["relay"] = relay ?? "undefined";
        }
        return {
            id: this.transport?.id ?? "undefined",
            connectionState: this.transport?.connectionState ?? "undefined",
            closed: this.transport?.closed ?? false,
            turnForced: this.turnForced(),
            failedToConnect: this.isFailedToConnect(),
            ...statsReport,
        };
    }
    isFailedToConnect() {
        return this.failedToConnect;
    }
    isClosed() {
        return (this.transport?.closed ||
            this.transport?.handler?._pc?.connectionState === "closed");
    }
    refreshTURNCredentials = async () => {
        if (!this.signaling) {
            Logger.error("Signaling is not available to refresh TURN credentials");
            return;
        }
        this.restartIce(IceRestartReason.RefreshTURNCredentials);
        if (this.refreshTURNCredentialsTimer) {
            clearTimeout(this.refreshTURNCredentialsTimer);
        }
    };
    resetTURNCredentialTimer = () => {
        if (this.refreshTURNCredentialsTimer) {
            clearTimeout(this.refreshTURNCredentialsTimer);
        }
        this.refreshTURNCredentialsTimer = setTimeout(this.refreshTURNCredentials, CLIENT_TURN_REFRESH_TIME_S * 1000);
    };
}
//# sourceMappingURL=SignaledTransport.js.map